/**
 * analytics.ts — tracker de analítica web propia (first-party).
 *
 * Reporta al backend (POST /events) las vistas de página del SITIO PÚBLICO y los
 * clicks de CTA (p.ej. "registrarse"). Alimenta el panel /admin → Analítica.
 *
 * Diseño:
 *  - Fire-and-forget: usa navigator.sendBeacon (no bloquea, sobrevive a la
 *    navegación); cae a fetch keepalive si no hay sendBeacon.
 *  - Sólo el sitio público (marketing). NO trackea la app interna ni la app móvil
 *    nativa (IS_NATIVE): esas vistas no son "visitas web".
 *  - Nunca rompe la web: todo va envuelto en try/catch silencioso.
 *  - Privacidad: el backend guarda sólo un hash anónimo del visitante, no la IP.
 */
import { API_BASE, IS_NATIVE } from './base';

/**
 * Prefijos de rutas del sitio de marketing que medimos. La app interna
 * (/inicio, /proyectos, /admin, /facturacion, …) NO se trackea acá.
 */
const PUBLIC_PREFIXES = [
  '/registro',
  '/login',
  '/recuperar',
  '/restablecer',
  '/invite',
  '/verificar-email',
  '/ayuda',
  '/sobre-nosotros',
  '/contacto',
  '/soporte',
  '/privacidad',
  '/privacy',
  '/terminos',
  '/terms',
  '/cookies',
];

/** ¿La ruta pertenece al sitio público de marketing? */
export function isPublicPath(path: string): boolean {
  if (path === '/') return true;
  return PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
}

function send(type: 'pageview' | 'cta', label: string): void {
  if (IS_NATIVE) return; // la analítica es del sitio web, no de la app móvil
  try {
    const referrer =
      typeof document !== 'undefined' && document.referrer ? document.referrer : undefined;
    const body = JSON.stringify({ type, label, referrer });
    const url = `${API_BASE}/events`;

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // la analítica nunca rompe ni frena la web
  }
}

/** Registra una vista de página (sólo si es una ruta pública del sitio). */
export function trackPageview(path: string): void {
  if (!isPublicPath(path)) return;
  send('pageview', path);
}

/** Registra un click de CTA (p.ej. 'register' al ir a registrarse). */
export function trackCta(label: string): void {
  send('cta', label);
}
